import { openDB, deleteDB, type IDBPDatabase } from 'idb'

// Offline basemap asset cache (tiles / glyphs / sprites). Deliberately a SEPARATE database from
// offlineStore: map assets carry no PII, and wipeOfflineData() must not touch this.
// Keys are logical (`tile/{z}/{x}/{y}`, `glyph/{fontstack}/{range}`, `sprite/{file}`), never URLs.

export const MAP_CACHE_DB = 'canvasser-map-cache'
export const MAP_CACHE_CAP_BYTES = 50 * 1024 * 1024

const STORE = 'assets'
/** A get refreshes recency (LRU) at most this often per row, and off the read path. */
export const RECENCY_REFRESH_MS = 60_000

interface AssetRow {
  key: string
  data: ArrayBuffer
  bytes: number
  usedAt: number
}

let dbPromise: Promise<IDBPDatabase> | null = null

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    const p = openDB(MAP_CACHE_DB, 1, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' })
        store.createIndex('usedAt', 'usedAt')
      },
      // [T5] The browser dropped the connection (WebKit after backgrounding, storage cleared): a dead
      // cached connection would make every get null and every put throw. Reopen on the next call.
      terminated() {
        if (dbPromise === p) dbPromise = null
      },
    })
    dbPromise = p
    // A failed open (private mode etc.) must not stick: allow a retry next call.
    p.catch(() => {
      if (dbPromise === p) dbPromise = null
    })
  }
  return dbPromise
}

/** A closed/dead connection surfaces as InvalidStateError when a transaction is started on it. */
function isDeadConnection(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'InvalidStateError'
}

/** Run `fn` on the connection; on a dead connection drop it and retry ONCE on a fresh one. */
async function withDb<T>(fn: (db: IDBPDatabase) => Promise<T>): Promise<T> {
  const p = getDb()
  try {
    return await fn(await p)
  } catch (err) {
    if (!isDeadConnection(err)) throw err
    if (dbPromise === p) dbPromise = null
    return fn(await getDb())
  }
}

const pendingTouches = new Set<Promise<void>>()

/** Refresh a row's recency in its own readwrite transaction. Not awaited by readers; failures ignored. */
function touch(key: string): void {
  const t = withDb(async (db) => {
    const tx = db.transaction(STORE, 'readwrite')
    const store = tx.objectStore(STORE)
    // Re-read inside the write so a newer put (fresh data) is never overwritten with the old row.
    const row = (await store.get(key)) as AssetRow | undefined
    if (row) await store.put({ ...row, usedAt: Date.now() })
    await tx.done
  }).catch(() => {
    /* non-fatal: recency is a hint */
  })
  pendingTouches.add(t)
  void t.finally(() => pendingTouches.delete(t))
}

/** Look up an asset. Read-only (lookups never serialise behind each other); recency is bumped in a
 *  separate, non-awaited write at most every RECENCY_REFRESH_MS. Resolves null on a miss OR any DB failure. */
export async function getCachedAsset(key: string): Promise<ArrayBuffer | null> {
  try {
    const row = await withDb((db) => db.get(STORE, key) as Promise<AssetRow | undefined>)
    if (!row) return null
    if (Date.now() - row.usedAt >= RECENCY_REFRESH_MS) touch(key)
    return row.data
  } catch {
    return null
  }
}

/** Store an asset. Throws on DB failure; callers treat that as "not cached". */
export async function putCachedAsset(key: string, data: ArrayBuffer): Promise<void> {
  const row: AssetRow = { key, data, bytes: data.byteLength, usedAt: Date.now() }
  await withDb(async (db) => {
    await db.put(STORE, row)
  })
}

/** True only if every key is cached. Resolves false on any DB failure. Does not bump recency. */
export async function hasCachedAssets(keys: string[]): Promise<boolean> {
  try {
    return await withDb(async (db) => {
      const tx = db.transaction(STORE, 'readonly')
      const store = tx.objectStore(STORE)
      const counts = await Promise.all(keys.map((k) => store.count(k)))
      await tx.done
      return counts.every((c) => c > 0)
    })
  } catch {
    return false
  }
}

/** Evict least-recently-used rows until total bytes <= capBytes. Returns bytes evicted. */
export function evictMapCacheToCap(capBytes: number = MAP_CACHE_CAP_BYTES): Promise<number> {
  return withDb((db) => evictOn(db, capBytes))
}

async function evictOn(db: IDBPDatabase, capBytes: number): Promise<number> {
  const tx = db.transaction(STORE, 'readwrite')
  const index = tx.objectStore(STORE).index('usedAt')

  let total = 0
  for (let c = await index.openCursor(); c; c = await c.continue()) {
    total += (c.value as AssetRow).bytes
  }
  let evicted = 0
  if (total > capBytes) {
    for (let c = await index.openCursor(); c && total > capBytes; c = await c.continue()) {
      const bytes = (c.value as AssetRow).bytes
      await c.delete()
      total -= bytes
      evicted += bytes
    }
  }
  await tx.done
  return evicted
}

/** Test helper: wait for the non-awaited recency writes `getCachedAsset` started. */
export async function __flushMapCacheTouchesForTests(): Promise<void> {
  await Promise.all([...pendingTouches])
}

/** Test helper: the open connection (null when none), to simulate the browser dropping it. */
export function __mapCacheDbForTests(): Promise<IDBPDatabase> | null {
  return dbPromise
}

/** Test helper: drop the connection and delete the database. */
export async function __resetMapCacheForTests(): Promise<void> {
  await __flushMapCacheTouchesForTests()
  if (dbPromise) {
    try {
      ;(await dbPromise).close()
    } catch {
      /* ignore */
    }
    dbPromise = null
  }
  await deleteDB(MAP_CACHE_DB)
}
